import type {
  ColumnFiltersState,
  PaginationState,
  SortingState,
} from '@tanstack/react-table'
import { applyCardFilters } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsCardView/documentCardFilterBar/applyCardFilters'
import type { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { columnFiltersToCardFilters } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentSearchResultFilters'
import { compareRevisionCodes } from 'features/knowledgeDocumentViewer/utils/revisionHelpers'

const documentTableStringCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
})

function compareNullableStrings(
  a: string | null | undefined,
  b: string | null | undefined
): number {
  return documentTableStringCollator.compare(a ?? '', b ?? '')
}

function compareNullableNumbers(
  a: number | null | undefined,
  b: number | null | undefined
): number {
  const left = a ?? 0
  const right = b ?? 0
  return left - right
}

function compareDocumentTableRows(
  a: DocumentTableRow,
  b: DocumentTableRow,
  columnId: string
): number {
  switch (columnId) {
    case 'title':
      return compareNullableStrings(a.title, b.title)
    case 'documentTitle':
      return compareNullableStrings(a.documentTitle, b.documentTitle)
    case 'numPages':
      return compareNullableNumbers(a.numPages, b.numPages)
    case 'system':
      return compareNullableStrings(a.system, b.system)
    case 'vendor':
      return compareNullableStrings(a.vendor, b.vendor)
    case 'docType':
      return compareNullableStrings(a.docType, b.docType)
    case 'drawing':
      return compareNullableStrings(a.drawing, b.drawing)
    case 'revisionCode':
      return compareRevisionCodes(a.revisionCode ?? '', b.revisionCode ?? '')
    case 'revisionDate':
      return compareNullableStrings(a.revisionDate, b.revisionDate)
    default:
      return 0
  }
}

export function filterDocumentTableRows(
  documents: DocumentTableRow[],
  columnFilters: ColumnFiltersState | undefined
): DocumentTableRow[] {
  return applyCardFilters(documents, columnFiltersToCardFilters(columnFilters))
}

export function sortDocumentTableRows(
  documents: DocumentTableRow[],
  sorting: SortingState
): DocumentTableRow[] {
  if (sorting.length === 0) return documents
  const sorted = [...documents]
  sorted.sort((a, b) => {
    for (const sort of sorting) {
      const result = compareDocumentTableRows(a, b, sort.id)
      if (result !== 0) return sort.desc ? -result : result
    }
    return 0
  })
  return sorted
}

export function paginateDocumentTableRows(
  documents: DocumentTableRow[],
  pagination: PaginationState
): DocumentTableRow[] {
  const start = pagination.pageIndex * pagination.pageSize
  return documents.slice(start, start + pagination.pageSize)
}

export function getDocumentTablePage(args: {
  documents: DocumentTableRow[]
  columnFilters: ColumnFiltersState | undefined
  sorting: SortingState
  pagination: PaginationState
}): {
  filteredCount: number
  pageRows: DocumentTableRow[]
} {
  const filtered = filterDocumentTableRows(args.documents, args.columnFilters)
  const sorted = sortDocumentTableRows(filtered, args.sorting)
  return {
    filteredCount: filtered.length,
    pageRows: paginateDocumentTableRows(sorted, args.pagination),
  }
}

export type DocumentTableColumnUniqueValues = {
  vendor: string[]
  docType: string[]
}

export function getDocumentTableColumnUniqueValues(
  documents: DocumentTableRow[]
): DocumentTableColumnUniqueValues {
  const vendor = new Set<string>()
  const docType = new Set<string>()
  for (const doc of documents) {
    if (doc.vendor) vendor.add(doc.vendor)
    if (doc.docType) docType.add(doc.docType)
  }
  return {
    vendor: Array.from(vendor).sort((a, b) =>
      documentTableStringCollator.compare(a, b)
    ),
    docType: Array.from(docType).sort((a, b) =>
      documentTableStringCollator.compare(a, b)
    ),
  }
}
